import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Preferences } from '@capacitor/preferences';

import { useAuthStore } from '@/stores/AuthState';
import { useUiSignals } from '@/stores/uiSignals';
import { useTranslation } from '@/utils/i18n';
import { toast } from '@/stores/toast';
import { resolveNotificationTarget } from '@/utils/notificationRouting';
import { applyNotificationTarget } from '@/utils/applyNotificationTarget';
import { keysToInvalidate, recoToBump } from '@/utils/notificationInvalidation';
import { bumpCommentCount } from '@/utils/commentCountCache';
import {
  initPushNotifications,
  registerForPush,
  getPushPermissionStatus,
  addPushListeners,
} from '@/services/pushNotifications';

/** Per-device flag: the soft-ask modal was already shown once. */
const PUSH_PROMPT_SEEN_KEY = 'push_prompt_seen_v1';

/**
 * Push bootstrap for the authenticated area (_tabs layout):
 *  - permission already granted → silent re-register (keeps last_used fresh)
 *  - never asked before → surface the soft-ask modal (showPrompt); the OS
 *    prompt fires only on accept()
 *  - previously dismissed or OS-denied → do nothing (Profile → Notifications
 *    remains the re-entry point)
 */
export function usePushNotifications() {
  const userId = useAuthStore((s) => s.userInfo.id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPrompt, setShowPrompt] = useState(false);
  const attemptedFor = useRef<number | null>(null);

  // A2 — a tapped push (warm or cold start) routes to the right place.
  const onTap = useCallback(
    (data: Record<string, string>) => {
      const target = resolveNotificationTarget(data);
      if (!target) return;
      // reco-comment-replies v2 — a reply lands by SIGNALS, not by URL: select
      // the circle, land on the card, open the conversation there. The same
      // chain `openSongCard` already performs, so there is no thread route.
      applyNotificationTarget(target, {
        navigate: (route) => void navigate({ to: route }),
        signals: useUiSignals.getState(),
      });
    },
    [navigate],
  );
  const onTapRef = useRef(onTap);
  useEffect(() => {
    onTapRef.current = onTap;
  }, [onTap]);

  // A8 — a push means server state moved. Refresh exactly the caches that
  // payload touches (see `utils/notificationInvalidation.ts`) instead of the
  // blanket `invalidateQueries()` that would reshuffle the discover deck under
  // the user's finger. Fires on foreground receive AND on tap.
  const onData = useCallback(
    (data: Record<string, string>) => {
      for (const queryKey of keysToInvalidate(data)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      // discover-card-polish — une réponse de quelqu'un d'autre fait bouger la
      // pastille par ÉCRITURE dans le cache, pas par invalidation : le feed est
      // un useInfiniteQuery, le refetcher rebattrait le deck sous le doigt.
      // comment-circle-scope — le cercle vient de la charge utile : une réponse
      // écrite dans un cercle privé n'a rien à faire dans le compteur d'un autre.
      const reply = recoToBump(data);
      if (reply) bumpCommentCount(queryClient, reply.recoId, reply.groupId);
    },
    [queryClient],
  );
  const onDataRef = useRef(onData);
  useEffect(() => {
    onDataRef.current = onData;
  }, [onData]);

  useEffect(() => {
    if (!userId || attemptedFor.current === userId) return;
    attemptedFor.current = userId;

    // Refs, not the callbacks themselves: the listener set is attached once per
    // user and must not be torn down when `navigate`/`queryClient` identity
    // changes (a cold-start tap event would be lost in the gap).
    const removeListeners = addPushListeners({
      onTap: (data) => onTapRef.current(data),
      onData: (data) => onDataRef.current(data),
    });

    void (async () => {
      const status = await getPushPermissionStatus();
      if (status === 'granted') {
        await initPushNotifications();
      } else if (status === 'prompt') {
        const { value } = await Preferences.get({ key: PUSH_PROMPT_SEEN_KEY });
        if (!value) setShowPrompt(true);
      }
      // 'denied' and null (web): nothing to do automatically.
    })();

    return removeListeners;
  }, [userId]);

  const accept = useCallback(async () => {
    setShowPrompt(false);
    await Preferences.set({ key: PUSH_PROMPT_SEEN_KEY, value: '1' });
    const ok = await registerForPush();
    if (ok) toast.success(t('profile.notifications.registerSuccess'));
  }, [t]);

  const decline = useCallback(() => {
    setShowPrompt(false);
    void Preferences.set({ key: PUSH_PROMPT_SEEN_KEY, value: '1' });
  }, []);

  return { showPrompt, accept, decline };
}
